<template>
  <div class="row justify-between items-center q-pa-sm bg-grey-2 border-bottom sticky-top">
    <div class="text-caption text-grey-7 text-weight-bold">PLAYER REPORTS</div>
    <q-btn
      icon="print"
      label="Print Report"
      color="primary"
      unelevated
      size="sm"
      @click="printPlayerSummary"
      :disable="playerSummaries.length === 0"
    />
  </div>
  <q-list separator>
    <q-expansion-item
      v-for="player in playerSummaries"
      :key="'player-' + player.id"
      expand-separator
    >
      <template v-slot:header>
        <q-item-section avatar>
          <q-avatar color="primary" text-color="white" size="sm" font-size="0.75rem">
            #{{ player.uniformNumber }}
          </q-avatar>
        </q-item-section>
        <q-item-section>
          <div class="text-weight-bold">{{ player.name }}</div>
        </q-item-section>
        <q-item-section side>
          <q-badge :color="player.batting.hitsLineup.length > 0 ? 'accent' : 'grey-4'">
            {{ player.batting.hitsLineup.length }} BIP
          </q-badge>
        </q-item-section>
      </template>

      <q-card class="bg-grey-1">
        <q-card-section class="q-pt-none q-pb-md q-px-md text-body2">
          <!-- PITCHING -->
          <div v-if="player.pitching" class="q-mb-md q-mt-sm">
            <div class="text-caption text-weight-bold text-primary text-uppercase q-mb-xs">
              Pitching Performance
            </div>
            <div class="row q-col-gutter-xs">
              <div class="col-6">
                Pitches: <strong>{{ player.pitching.total }}</strong>
              </div>
              <div class="col-6">
                Strikes / Balls:
                <strong>{{ player.pitching.strikes }} / {{ player.pitching.balls }}</strong>
              </div>
              <div class="col-6">
                Wild Pitches: <strong>{{ player.pitching.wildPitches }}</strong>
              </div>
              <div class="col-6">
                Whiff Rate: <strong>{{ player.pitching.whiffRate }}%</strong>
              </div>
            </div>
          </div>

          <!-- BATTING -->
          <div class="q-mb-md" :class="{ 'q-mt-sm': !player.pitching }">
            <div class="text-caption text-weight-bold text-primary text-uppercase q-mb-xs">
              At Bats <strong>{{ player.batting.plateAppearances }}</strong>
            </div>
            <div>
              Pitches Seen: <strong>{{ player.batting.pitchesSeen }}</strong> ({{
                player.batting.twoStrikePitches
              }}
              with 2 strikes)
            </div>
            <div>
              Hits: <strong>{{ player.batting.hits }}</strong>
            </div>
            <q-card
              v-if="player.batting.hitsLineup.length > 0"
              class="bg-white q-mt-sm border-all rounded-borders"
            >
              <q-card-section class="q-py-sm q-px-md">
                <div
                  v-for="hit in player.batting.hitsLineup"
                  :key="hit.id"
                  class="q-mb-sm q-mt-xs border-bottom q-pb-xs"
                >
                  <div class="row items-center no-wrap">
                    <div class="col-auto q-mr-sm">
                      <q-badge color="grey-8" outline>{{ hit.inning }}</q-badge>
                    </div>
                    <div class="col text-body2 text-weight-medium">
                      {{ hit.outcome }}
                      <span class="text-grey-7 text-caption text-weight-regular">
                              ( {{ hit.description }} )
                            </span>
                    </div>
                    <div
                      class="col-auto text-warning text-caption q-ml-sm"
                      v-if="hit.quality > 1"
                    >
                      {{ '★'.repeat(hit.quality) }}
                    </div>
                  </div>
                  <div v-if="hit.fielders" class="text-caption text-grey-8 q-ml-lg q-mt-xs">
                    Fielding:
                    <span class="text-weight-bold text-primary">{{ hit.fielders }}</span>
                  </div>
                </div>
              </q-card-section>
            </q-card>
            <q-card v-else class="bg-white q-mt-sm border-all rounded-borders">
              <q-card-section class="q-py-sm q-px-md text-caption text-grey-6 text-italic">
                No balls in play today.
              </q-card-section>
            </q-card>
          </div>

          <!-- BASERUNNING -->
          <div v-if="player.baserunningNotes.length > 0" class="q-mb-md">
            <div class="text-caption text-weight-bold text-primary text-uppercase q-mb-xs">
              Baserunning Notes
            </div>
            <ul class="q-pl-md q-my-none q-mb-xs text-grey-8">
              <li v-for="(note, idx) in player.baserunningNotes" :key="'br-' + idx">
                {{ note }}
              </li>
            </ul>
          </div>

          <!-- FIELDING MECHANICS -->
          <div v-if="player.fieldingNotes.length > 0" class="q-mb-md">
            <div class="text-caption text-weight-bold text-primary text-uppercase q-mb-xs">
              Fielding Mechanics
            </div>
            <ul class="q-pl-md q-my-none q-mb-xs text-grey-8">
              <li v-for="(note, idx) in player.fieldingNotes" :key="'fld-' + idx">
                {{ note }}
              </li>
            </ul>
          </div>

          <!-- FIELDING PLAYS -->
          <div v-if="player.fieldingPlays.length > 0" class="q-mb-md">
            <div class="text-caption text-weight-bold text-primary text-uppercase q-mb-xs">
              Fielding Plays
            </div>
            <q-card class="bg-white border-all rounded-borders">
              <q-card-section class="q-py-sm q-px-md">
                <div
                  v-for="play in player.fieldingPlays"
                  :key="play.id"
                  class="q-mb-sm q-mt-xs border-bottom q-pb-xs"
                >
                  <!-- Hit Details -->
                  <div class="row items-center no-wrap">
                    <div class="col-auto q-mr-sm">
                      <q-badge color="grey-8" outline>Inn {{ play.inning }}</q-badge>
                    </div>
                    <div class="col text-body2 text-weight-medium">
                      In Play
                      <span class="text-grey-7 text-caption text-weight-regular">
                              ( {{ play.hitDescription }} )
                            </span>
                    </div>
                    <div class="col-auto text-warning text-caption q-ml-sm">
                      {{ '★'.repeat(play.hitQuality) }}
                    </div>
                  </div>

                  <!-- Fielder Details -->
                  <div class="text-caption text-grey-8 q-ml-lg q-mt-xs">
                    Fielding:
                    <span class="text-weight-bold">
                            <template v-for="(s, idx) in play.sequenceRaw" :key="idx">
                              <span
                                :class="
                                  s.position === play.playerPos
                                    ? 'text-primary bg-blue-1 q-px-xs rounded-borders'
                                    : 'text-grey-8'
                                "
                              >
                                {{ s.position }}{{ s.isError ? 'E' : ''
                                }}{{ s.role === 'bypassed' ? '(Miss)' : '' }}
                              </span>
                              <span v-if="Number(idx) < play.sequenceRaw.length - 1">-</span>
                            </template>
                          </span>
                    <span class="q-ml-xs text-weight-bold text-black"
                    >: {{ play.outcome }}</span
                    >
                    <span class="text-warning q-ml-sm">{{
                        '★'.repeat(play.difficulty)
                      }}</span>
                  </div>
                </div>
              </q-card-section>
            </q-card>
          </div>

          <!-- SCOUTING -->
          <div v-if="player.scoutingNotes.length > 0">
            <div class="text-caption text-weight-bold text-primary text-uppercase q-mb-xs">
              Scouting / Notebook
            </div>
            <ul class="q-pl-md q-my-none q-mb-xs text-grey-8">
              <li v-for="(note, idx) in player.scoutingNotes" :key="'sct-' + idx">
                {{ note }}
              </li>
            </ul>
          </div>

          <!-- EMPTY STATE -->
          <div
            v-if="
                    !player.pitching &&
                    player.baserunningNotes.length === 0 &&
                    player.fieldingNotes.length === 0 &&
                    player.scoutingNotes.length === 0 &&
                    player.fieldingPlays.length === 0
                  "
            class="text-grey-6 text-caption text-italic q-mt-sm"
          >
            No advanced mechanics, plays, or notes recorded today.
          </div>
        </q-card-section>
      </q-card>
    </q-expansion-item>

    <div v-if="playerSummaries.length === 0" class="q-pa-xl text-center text-grey-6">
      <q-icon name="group" size="xl" color="grey-4" class="q-mb-sm" />
      <div>No players found for this selection.</div>
    </div>
  </q-list>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useGameStore } from 'stores/game-store'
import { useSettingsStore } from 'stores/settings-store'
import {calculateFieldingSide} from 'src/helpers/helpers'
import type {
  BaseballPlayer,
  PitchTransaction,
  PlayTransaction, RunnerAdvanceTransaction,
} from 'src/stores/base-types'

const gameStore = useGameStore()
const settingsStore = useSettingsStore()

const props = defineProps<{
  teamLineup: 'home' | 'away' | 'all'
}>()

// --- HIT LOCATION TRANSLATOR HELPER ---
const translateHitLocation = (
  relX: number | null,
  relY: number | null,
  trajectory: string | null,
) => {
  if (relX === null || relY === null) return 'Unknown Location'
  const { zone, depth } = gameStore.getHitClassification(relX, relY)

  let horizontal = 'Center'
  if (relX < 0.35) horizontal = 'Left Side'
  else if (relX > 0.65) horizontal = 'Right Side'

  const isInfield = relY < 0.45
  const vertical = isInfield ? 'Infield' : 'Outfield'

  let depthStr = ''
  if (depth === 'S') depthStr = 'Shallow'
  else if (depth === 'M') depthStr = 'Medium'
  else if (depth === 'D') depthStr = 'Deep'

  const trajLabel =
    settingsStore.primaryFieldingTrajectories?.find((t) => t.value === trajectory)?.label || 'Hit'
  const locStr = isInfield
    ? `${depthStr} ${horizontal}`
    : `${depthStr} ${vertical}, ${horizontal.replace(' Side', '')}`
  return `${trajLabel} to ${locStr} (Zone ${zone})`
}

// --- ADVANCED PLAYER SUMMARY LOGIC ---
const playerSummaries = computed(() => {
  const lineupsToScan = []
  if (props.teamLineup === 'away' || props.teamLineup === 'all')
    lineupsToScan.push(gameStore.visitingLineup || [])
  if (props.teamLineup === 'home' || props.teamLineup === 'all')
    lineupsToScan.push(gameStore.homeLineup || [])

  const uniquePlayers = new Map<number, BaseballPlayer>()
  lineupsToScan.forEach((lineup) => {
    lineup.forEach((entry) => {
      if (entry.player?.id) uniquePlayers.set(entry.player.id, entry.player)
      if (entry.substitutions) {
        entry.substitutions.forEach((sub) => {
          if (sub.player?.id) uniquePlayers.set(sub.player.id, sub.player)
        })
      }
    })
  })

  const summaries = []
  const allOutcomes = [
    ...(settingsStore.playAllOutcomes || []),
    ...(settingsStore.primaryPitchCategories || []),
    ...(settingsStore.pitchAdvancedOptions || []),
  ]

  for (const player of uniquePlayers.values()) {
    const pid = player.id

    // PITCHING EXTRACTION
    const pitchesThrown = gameStore.transactions.filter(
      (t) => t.eventType === 'PITCH' && t.pitcherId === pid,
    ) as PitchTransaction[]
    let pitchingStats = null

    if (pitchesThrown.length > 0) {
      let balls = 0,
        strikes = 0,
        wildPitches = 0,
        swings = 0,
        whiffs = 0
      pitchesThrown.forEach((p) => {
        const out = p.pitchOutcome || ''
        if (['B', 'W', 'I', 'V', 'P', 'H'].includes(out)) balls++
        if (['C', 'S', 'F', 'T', 'K', 'Q', 'O', 'R', 'A', 'X'].includes(out)) strikes++
        if (out === 'W') wildPitches++
        if (['S', 'F', 'T', 'X', 'K', 'Q', 'O', 'R'].includes(out)) swings++
        if (['S', 'K', 'Q'].includes(out)) whiffs++
      })
      pitchingStats = {
        total: pitchesThrown.length,
        balls,
        strikes,
        wildPitches,
        whiffRate: swings > 0 ? Math.round((whiffs / swings) * 100) : 0,
      }
    }

    // HITs EXTRACTION (Lineup format)
    const hitTxns = gameStore.transactions.filter(
      (t) => t.eventType === 'PITCH' && t.batterId === pid && t.hitLocationX != null,
    ) as PitchTransaction[]

    const hitsLineup = hitTxns.map((t) => {
      const pa = gameStore.plateAppearances.find((p) => p.id === t.plateAppearanceId)
      const rawOutcome = t.outcome || t.pitchOutcome
      let readableOutcome = rawOutcome
      const found = allOutcomes.find((o) => o.value === rawOutcome)
      if (found) readableOutcome = found.label

      const playTxn = gameStore.transactions.find(
        (pt) => pt.eventType === 'PLAY' && pt.plateAppearanceId === t.plateAppearanceId,
      ) as PlayTransaction | undefined

      let readableOutcomePlay = ''
      let fielders = ''
      if (playTxn) {
        const foundPlay = allOutcomes.find((o) => o.value === playTxn.outcome)
        readableOutcomePlay = foundPlay ? foundPlay.label : playTxn.outcome

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const seq = (playTxn as any).playSequence
        if (seq && seq.length > 0) {
          fielders = seq
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((s: any) => {
              let p = String(s.position)
              if (s.isError) p += 'E'
              if (s.role === 'bypassed') return `[${p} Missed]`
              return p
            })
            .join('-')
        }
        if (readableOutcomePlay !== '') {
          fielders += ': ' + readableOutcomePlay
        }
      }

      return {
        id: t.id,
        inning: pa ? `Inn ${pa.inning}` : '',
        outcome: readableOutcome,
        quality: t.hitQualityRating || 1,
        description: translateHitLocation(
          t.hitLocationX || null,
          t.hitLocationY || null,
          t.battedBallTrajectory || null,
        ),
        fielders: fielders,
      }
    })

    // BATTING EXTRACTION
    const playerPAs = gameStore.plateAppearances.filter((pa) => pa.batterId === pid)
    const plateAppearanceCount = playerPAs.length

    const pitchesSeen = gameStore.transactions.filter(
      (t) => t.eventType === 'PITCH' && t.batterId === pid,
    ) as PitchTransaction[]
    const hits: string[] = []

    const hitPitches = pitchesSeen.filter((p) => p.hitLocationX != null)
    const hitPitchIds = hitPitches.map((p) => String(p.id))
    const linkedPlays = gameStore.transactions.filter(
      (t) => t.eventType === 'PLAY' && hitPitchIds.includes(String(t.linkedPitchId)),
    ) as PlayTransaction[]

    hitPitches.forEach((pitch) => {
      const play = linkedPlays.find((pl) => String(pl.linkedPitchId) === String(pitch.id))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const outcome = play?.outcome || (pitch as any).outcome || pitch.pitchOutcome || 'X'
      if (['S', 'D', 'T', 'HR'].includes(outcome)) {
        const label = allOutcomes.find((o) => o.value === outcome)?.label || outcome
        hits.push(label)
      }
    })

    let twoStrikePitches = 0
    const paGroups: Record<string, PitchTransaction[]> = {}
    pitchesSeen.forEach((p) => {
      const paId = String(p.plateAppearanceId)
      if (!paGroups[paId]) paGroups[paId] = []
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      paGroups[paId]!.push(p)
    })

    Object.values(paGroups).forEach((paPitches) => {
      paPitches.sort((a, b) => a.sequence - b.sequence)
      let s = 0
      paPitches.forEach((p) => {
        if (s >= 2) twoStrikePitches++
        const out = p.pitchOutcome || ''
        if (['C', 'S', 'F', 'T', 'K', 'Q', 'O', 'R', 'A'].includes(out)) {
          if (s < 2 || ['C', 'S', 'K', 'Q', 'A'].includes(out)) s++
        }
      })
    })

    // BASERUNNING EXTRACTION
    const baserunningTxns = gameStore.transactions.filter(
      (t) => t.eventType === 'RUNNER_ADVANCE' && t.runnerId === pid,
    ) as RunnerAdvanceTransaction[]
    const baserunningNotes: string[] = []
    baserunningTxns.forEach((r) => {
      const notes = []
      if (r.baserunningJump && r.baserunningJump !== 'routine')
        notes.push(`Jump: ${r.baserunningJump}`)
      if (r.baserunningDecision && r.baserunningDecision !== 'not_required')
        notes.push(`Decision: ${r.baserunningDecision}`)

      if (notes.length > 0) {
        baserunningNotes.push(
          `To ${r.endBase === 'H' ? 'Home' : r.endBase + 'B'}: ${notes.join(' | ')}`,
        )
      }
    })

    // FIELDING MECHANICS & FIELDING PLAYS EXTRACTION
    const fieldingNotes: string[] = []
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fieldingPlays: any[] = []

    const allPlays = gameStore.transactions.filter(
      (t) => t.eventType === 'PLAY' || t.eventType === 'RUNNER_ADVANCE',
    )

    allPlays.forEach((play) => {
      const pa = gameStore.plateAppearances.find((p) => p.id === play.plateAppearanceId)
      if (!pa || !pa.defenseSnapshot) return

      let playerPos = null
      for (const [pos, id] of Object.entries(pa.defenseSnapshot)) {
        if (id.playerId === pid) {
          playerPos = Number(pos)
          break
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (playerPos !== null && (play as any).playSequence) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const seqItem = (play as any).playSequence.find((s: any) => s.position === playerPos)

        if (seqItem) {
          const notes = []
          const isRunnerAdvance = play.eventType === 'RUNNER_ADVANCE'
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const rReason = isRunnerAdvance ? ((play as any).advanceReason || '') : ''
          const isPO = ['PICKOFF', 'PO'].includes(rReason)
          const isSB = ['SB', 'STOLEN_BASE'].includes(rReason)

          let prefix = ''
          if (isPO) prefix = 'PICKOFF '
          if (isSB) prefix = 'STOLEN BASE '

          const formatLabel = (str: string) => {
            if (!str) return ''
            return str.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
          }

          const roleStr = `Role: ${formatLabel(seqItem.role)}`
          let gloveInfo = ''

          if ((seqItem.role === 'fielded' || seqItem.role ==='missed_play') && !isPO && !isSB) {
            let hitPitch = null
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((play as any).linkedPitchId) {
              hitPitch = gameStore.transactions.find(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (t) => String(t.id) === String((play as any).linkedPitchId)
              ) as PitchTransaction | undefined
            }

            if (hitPitch && hitPitch.hitLocationX != null && hitPitch.hitLocationY != null) {
              // Extract positions falling back to standard field map coordinate centers
              const defX = ([0, 0.5, 0.5, 0.65, 0.65, 0.35, 0.35, 0.25, 0.5, 0.75] as number[])[playerPos] || 0.5
              const defY = ([0, 0.6, 1.05, 0.65, 0.45, 0.65, 0.45, 0.25, 0.2, 0.25] as number[])[playerPos] || 0.5
              const fX = pa.defenseSnapshot[playerPos as 1|2|3|4|5|6|7|8|9]?.relX ?? defX
              const fY = pa.defenseSnapshot[playerPos as 1|2|3|4|5|6|7|8|9]?.relY ?? defY
              const throwingHand = player.throws || 'R'

              const sideCalc = calculateFieldingSide(fX, fY, hitPitch.hitLocationX, hitPitch.hitLocationY, throwingHand)
              gloveInfo = ` ${formatLabel(sideCalc.glove_hand)} dist:${sideCalc.dist.toFixed(3)}`
            }
          }

          notes.push(roleStr + gloveInfo)

          if (seqItem.approach && seqItem.approach !== 'not_scored') notes.push(`Approach: ${formatLabel(seqItem.approach)}`)
          if (seqItem.execution && seqItem.execution !== 'not_scored') notes.push(`Execution: ${formatLabel(seqItem.execution)}`)
          if (seqItem.transfer && seqItem.transfer !== 'not_required') notes.push(`Transfer: ${formatLabel(seqItem.transfer)}`)
          if (seqItem.throw && seqItem.throw !== 'not_required') notes.push(`Throw: ${formatLabel(seqItem.throw)}`)
          if (seqItem.decision && seqItem.decision !== 'not_required') notes.push(`Decision: ${formatLabel(seqItem.decision)}`)
          if (seqItem.isError) notes.push(`Error: ${formatLabel(seqItem.errorType)}`)

          let hitContext = ''
          if (isPO || isSB) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rOut = (play as any).isOut ? 'out' : 'safe'
            hitContext = `(Play outcome ${rOut})`
          } else {
            let hitPitch = null
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((play as any).linkedPitchId) {
              hitPitch = gameStore.transactions.find(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (t) => String(t.id) === String((play as any).linkedPitchId)
              ) as PitchTransaction | undefined
            }
            if (hitPitch && hitPitch.hitLocationX != null) {
              const desc = translateHitLocation(
                hitPitch.hitLocationX,
                hitPitch.hitLocationY || null,
                hitPitch.battedBallTrajectory ?? null
              )
              const stars = hitPitch.hitQualityRating && hitPitch.hitQualityRating > 1 ? ' ' + '★'.repeat(hitPitch.hitQualityRating) : ''
              const splitDesc = desc.split(' to ')

              if (splitDesc.length === 2) {
                hitContext = `( ${splitDesc[0]}${stars} to ${splitDesc[1]} )`
              } else {
                hitContext = `( ${desc}${stars} )`
              }
            }
          }

          if (notes.length > 0) {
            fieldingNotes.push(`Inn ${pa.inning} (Pos ${playerPos}): ${prefix}${notes.join(' | ')} ${hitContext}`.trim())
          }

          let hitPitchForPlays = null
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((play as any).linkedPitchId) {
            hitPitchForPlays = gameStore.transactions.find(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (t) => String(t.id) === String((play as any).linkedPitchId),
            ) as PitchTransaction
          }

          let readableOutcomePlay = ''
          if (play.eventType === 'RUNNER_ADVANCE') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            readableOutcomePlay = (play as any).isOut
              ? 'Out'
              : // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (play as any).endBase === 'H'
                ? 'Score'
                : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                `To ${(play as any).endBase}`
          } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const foundPlay = allOutcomes.find((o) => o.value === (play as any).outcome)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            readableOutcomePlay = foundPlay ? foundPlay.label : (play as any).outcome
          }

          let resolvedHitDesc = 'Unknown Location'
          if (isPO) resolvedHitDesc = 'Pickoff'
          else if (isSB) resolvedHitDesc = 'Stolen Base'
          else if (hitPitchForPlays) {
            resolvedHitDesc = translateHitLocation(
              hitPitchForPlays.hitLocationX ?? null,
              hitPitchForPlays.hitLocationY ?? null,
              hitPitchForPlays.battedBallTrajectory ?? null,
            )
          }

          fieldingPlays.push({
            id: play.id,
            inning: pa.inning,
            hitDescription: resolvedHitDesc,
            hitQuality: hitPitchForPlays?.hitQualityRating || 1,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            sequenceRaw: (play as any).playSequence,
            playerPos: playerPos,
            outcome: readableOutcomePlay,
            difficulty: seqItem.difficulty || 1,
          })
        }
      }
    })

    // SCOUTING / NOTEBOOK EXTRACTION
    const scoutingTxns = gameStore.transactions.filter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (t) => t.eventType === 'SCOUTING' && (t as any).targetPlayerIds?.includes(pid),
    )
    const scoutingNotes = scoutingTxns.map((t) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let text = (t as any).tagValue || ''
      const readableTag = settingsStore.activeScoutingTags.find((ts) => ts.value === text)
      text = readableTag ? readableTag.label : text
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((t as any).customNote) text += ` - ${(t as any).customNote}`
      return text
    })

    summaries.push({
      id: pid,
      name: player.name,
      uniformNumber: player.uniformNumber || '-',
      pitching: pitchingStats,
      batting: {
        plateAppearances: plateAppearanceCount,
        pitchesSeen: pitchesSeen.length,
        hits: hits.join(', ') || 'None',
        twoStrikePitches,
        hitsLineup,
      },
      baserunningNotes,
      fieldingNotes,
      fieldingPlays,
      scoutingNotes,
    })
  }

  return summaries.sort((a, b) => a.name.localeCompare(b.name))
})

// --- PRINTER FRIENDLY PLAYER SUMMARY REPORT ---
const printPlayerSummary = () => {
  const printWindow = window.open('', '_blank')
  if (!printWindow) {
    console.error('Pop-up blocked. Could not open print window.')
    return
  }

  const teamName =
    props.teamLineup === 'all'
      ? 'All Players'
      : props.teamLineup === 'home'
        ? gameStore.homeTeamName
        : gameStore.visitingTeamName

  // Base HTML and Print-Specific CSS
  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Player Summary Report - ${teamName}</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; line-height: 1.4; color: #000; padding: 20px; font-size: 12px; }
        h1 { margin-top: 0; padding-bottom: 10px; border-bottom: 2px solid #000; }

        .player-card { page-break-inside: avoid; margin-bottom: 25px; border: 1px solid #ccc; padding: 15px; border-radius: 8px; }

        .header { font-size: 16px; font-weight: bold; margin-bottom: 10px; border-bottom: 1px solid #000; padding-bottom: 5px; }
        .section-title { font-size: 11px; font-weight: bold; text-transform: uppercase; margin-top: 12px; margin-bottom: 4px; color: #444; border-bottom: 1px solid #eee;}

        .stat-grid { display: flex; flex-wrap: wrap; gap: 10px; }
        .stat-box { flex: 1 1 45%; }

        ul { margin: 0; padding-left: 20px; }
        li { margin-bottom: 3px; }

        .play-row { margin-bottom: 6px; border-bottom: 1px solid #f5f5f5; padding-bottom: 4px;}
        .stars { color: #d35400; font-size: 10px; }
        .highlight { font-weight: bold; background-color: #e0f7fa; padding: 2px 4px; border-radius: 4px; border: 1px solid #b2ebf2; }
      </style>
    </head>
    <body>
      <h1>Player Summary Report: ${teamName}</h1>
  `

  playerSummaries.value.forEach((player) => {
    html += `<div class="player-card">`
    html += `<div class="header">#${player.uniformNumber} - ${player.name}</div>`

    if (player.pitching) {
      html += `
        <div class="section-title">Pitching Performance</div>
        <div class="stat-grid">
          <div class="stat-box">Pitches: <strong>${player.pitching.total}</strong></div>
          <div class="stat-box">Strikes / Balls: <strong>${player.pitching.strikes} / ${player.pitching.balls}</strong></div>
          <div class="stat-box">Wild Pitches: <strong>${player.pitching.wildPitches}</strong></div>
          <div class="stat-box">Whiff Rate: <strong>${player.pitching.whiffRate}%</strong></div>
        </div>
      `
    }

    html += `
      <div class="section-title">At Bats <strong>${player.batting.plateAppearances}</strong></div>
      <div>Pitches Seen: <strong>${player.batting.pitchesSeen}</strong> (${player.batting.twoStrikePitches} with 2 strikes)</div>
      <div>Hits: <strong>${player.batting.hits}</strong></div>
    `

    if (player.batting.hitsLineup.length > 0) {
      html += `<ul>`
      player.batting.hitsLineup.forEach((hit) => {
        const stars = hit.quality > 1 ? `<span class="stars">${'★'.repeat(hit.quality)}</span>` : ''
        html += `<li><strong>${hit.inning}:</strong> ${hit.outcome} (${hit.description}) ${stars}`
        if (hit.fielders)
          html += `<br><span style="color:#555; font-size: 11px;">Fielding: ${hit.fielders}</span>`
        html += `</li>`
      })
      html += `</ul>`
    }

    if (player.baserunningNotes.length > 0) {
      html += `<div class="section-title">Baserunning Notes</div><ul>`
      player.baserunningNotes.forEach((note) => (html += `<li>${note}</li>`))
      html += `</ul>`
    }

    if (player.fieldingNotes.length > 0) {
      html += `<div class="section-title">Fielding Mechanics</div><ul>`
      player.fieldingNotes.forEach((note) => (html += `<li>${note}</li>`))
      html += `</ul>`
    }

    if (player.fieldingPlays.length > 0) {
      html += `<div class="section-title">Fielding Plays</div>`
      player.fieldingPlays.forEach((play) => {
        const hitStars = `<span class="stars">${'★'.repeat(play.hitQuality)}</span>`
        const fldStars = `<span class="stars">${'★'.repeat(play.difficulty)}</span>`

        let seqHtml = ''
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        play.sequenceRaw.forEach((s: any, idx: number) => {
          const isHighlight = s.position === play.playerPos
          const txt = `${s.position}${s.isError ? 'E' : ''}${s.role === 'bypassed' ? '(Miss)' : ''}`

          if (isHighlight) seqHtml += `<span class="highlight">${txt}</span>`
          else seqHtml += `<span>${txt}</span>`

          if (idx < play.sequenceRaw.length - 1) seqHtml += ' - '
        })

        html += `
          <div class="play-row">
            <strong>Inn ${play.inning}</strong>: In Play (${play.hitDescription}) ${hitStars}<br>
            <span style="color:#444;">Fielding: ${seqHtml} : <strong>${play.outcome}</strong> ${fldStars}</span>
          </div>
        `
      })
    }

    if (player.scoutingNotes.length > 0) {
      html += `<div class="section-title">Scouting / Notebook</div><ul>`
      player.scoutingNotes.forEach((note) => (html += `<li>${note}</li>`))
      html += `</ul>`
    }

    html += `</div>`
  })

  html +=
    ' <script> ' +
    ' window.onload = () => { ' +
    ' window.print() } ' +
    ' <\u002Fscript><\u002Fbody><\u002Fhtml>'

  printWindow.document.open()
  printWindow.document.write(html)
  printWindow.document.close()
}
</script>

<style scoped>
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}
.top-z {
  z-index: 10;
}
</style>
